/**
 * R13.1-C — Public Full Spectrum Center rooms directory.
 *
 * Route: /rooms (no auth required, no Turnstile).
 * Data source: GET /api/rooms/public — returns the 24 seeded community rooms
 * with safe fields only (room_id, slug, name, community_handle_url, status,
 * has_keyholder, locked).
 *
 * Testids per spec:
 *   - rooms-directory
 *   - rooms-filter-{all|live|available|inactive}
 *   - rooms-card-{slug}
 *   - rooms-card-{slug}-join
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, ArrowRight, Loader2, Mail, Users, Clock } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const FILTERS = [
  { slug: "recent",    label: "Recently Active" },   // R14-F default
  { slug: "all",       label: "All" },
  { slug: "live",      label: "Live" },
  { slug: "available", label: "Available" },
  { slug: "inactive",  label: "Inactive" },
];


// Deterministic gradient from slug (hash → two-color spec). Keeps each room
// card visually distinct without needing per-room avatars.
function gradFromSlug(slug) {
  let h = 0;
  for (let i = 0; i < slug.length; i++) h = ((h << 5) - h + slug.charCodeAt(i)) | 0;
  const a = Math.abs(h) % 360;
  const b = (a + 60 + (Math.abs(h >> 8) % 80)) % 360;
  return `linear-gradient(135deg, hsl(${a} 70% 55%) 0%, hsl(${b} 75% 45%) 100%)`;
}

function initialsFromName(name) {
  if (!name) return "?";
  const parts = name.split(/[\s&]+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

function prettyHandle(url) {
  try {
    const u = new URL(url);
    return u.hostname.replace(/^www\./, "") + u.pathname.replace(/\/$/, "");
  } catch { return url; }
}


export default function RoomsDirectory() {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("recent");   // R14-F default

  useEffect(() => {
    let cancelled = false;
    let timer;
    const tick = async () => {
      try {
        const r = await fetch(`${API}/api/rooms/public`);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = await r.json();
        if (!cancelled) {
          setRooms(j.rooms || []);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || String(err));
          setLoading(false);
        }
      }
    };
    tick();
    // R14-F — refresh live counts every 30s.
    timer = setInterval(tick, 30000);
    return () => { cancelled = true; if (timer) clearInterval(timer); };
  }, []);

  const counts = useMemo(() => ({
    recent:    rooms.filter((r) => (r.participant_count || 0) > 0 || r.status === "live").length,
    all:       rooms.length,
    live:      rooms.filter((r) => r.status === "live").length,
    available: rooms.filter((r) => r.status === "available").length,
    inactive:  rooms.filter((r) => r.status === "inactive").length,
  }), [rooms]);

  const filtered = useMemo(() => {
    let list = rooms;
    if (filter === "recent") {
      list = [...rooms].sort((a, b) => {
        const ac = a.participant_count || 0;
        const bc = b.participant_count || 0;
        if (ac !== bc) return bc - ac;
        const at = a.last_active_at ? new Date(a.last_active_at).getTime() : 0;
        const bt = b.last_active_at ? new Date(b.last_active_at).getTime() : 0;
        return bt - at;
      });
    } else if (filter !== "all") {
      list = rooms.filter((r) => r.status === filter);
    }
    return list;
  }, [rooms, filter]);

  return (
    <div className="min-h-screen bg-[#050508] text-white" data-testid="rooms-directory">
      <header className="border-b border-white/10 bg-[#080810]/85 backdrop-blur">
        <div className="max-w-6xl mx-auto px-6 py-10">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#00F0FF] mb-2">Full Spectrum Center — Community</div>
              <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Find your room.</h1>
            </div>
            <Link
              to="/leaderboard"
              className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-amber-300/85 hover:text-amber-200 px-3 py-2 rounded-md border border-amber-400/25 hover:border-amber-400/45 transition-colors"
              data-testid="rooms-directory-leaderboard-link"
            >
              View leaderboard →
            </Link>
          </div>
          <p className="text-sm text-white/55 mt-2 max-w-2xl">
            24 keyholder-run rooms, each with its own community handle. Click into a room
            to join. New rooms can apply via the bottom link.
          </p>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Filter pills */}
        <div className="flex flex-wrap gap-2 mb-6">
          {FILTERS.map((f) => (
            <button
              key={f.slug}
              onClick={() => setFilter(f.slug)}
              className={`px-3.5 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-[0.14em] border transition ${
                filter === f.slug
                  ? "bg-[#00F0FF]/15 border-[#00F0FF]/55 text-[#00F0FF]"
                  : "bg-white/[0.03] border-white/12 text-white/55 hover:text-white hover:bg-white/[0.06]"
              }`}
              data-testid={`rooms-filter-${f.slug}`}
            >
              {f.label}
              <span className="ml-2 text-[9px] opacity-60">{counts[f.slug]}</span>
            </button>
          ))}
        </div>

        {loading && (
          <div className="grid place-items-center py-16 text-white/55 text-sm gap-2 flex-col">
            <Loader2 className="w-5 h-5 animate-spin" />
            Loading rooms…
          </div>
        )}
        {error && (
          <div className="rounded-xl border border-rose-400/30 bg-rose-500/8 p-6 text-rose-200 text-sm">
            Failed to load: {error}
          </div>
        )}

        {!loading && !error && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((r) => <RoomCard key={r.room_id} r={r} />)}
            {filtered.length === 0 && (
              <div className="col-span-full rounded-xl border border-white/10 p-10 text-center text-white/45 text-sm">
                No rooms match this filter.
              </div>
            )}
          </div>
        )}

        <footer className="mt-14 pt-8 border-t border-white/8 text-center">
          <a
            href="mailto:ghadams562@gmail.com?subject=Full Spectrum Center%20—%20room%20application"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-white/55 hover:text-white"
            data-testid="rooms-directory-apply"
          >
            <Mail className="w-3.5 h-3.5" />
            Want your own room? Apply
          </a>
        </footer>
      </main>
    </div>
  );
}


function RoomCard({ r }) {
  const slug = r.slug || r.room_id;
  const initials = initialsFromName(r.name);
  const grad = gradFromSlug(slug);
  const canJoin = r.status === "live" || r.status === "available";

  return (
    <div
      className="rounded-xl border border-white/10 bg-[#0a0a14]/75 overflow-hidden hover:border-white/20 transition flex flex-col"
      data-testid={`rooms-card-${slug}`}
    >
      <div className="flex items-start gap-3 p-4">
        <div
          className="w-12 h-12 rounded-lg grid place-items-center text-base font-bold text-black/85 shrink-0"
          style={{ background: grad }}
        >
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-base leading-tight truncate">{r.name}</h3>
          <div className="mt-1 flex items-center gap-2 flex-wrap">
            <StatusPill status={r.status} />
            {r.locked && (
              <span className="text-[9px] uppercase tracking-[0.16em] text-amber-300 px-2 py-0.5 rounded bg-amber-400/12 border border-amber-400/25">Locked</span>
            )}
            <LiveCountChip count={r.participant_count || 0} />
          </div>
        </div>
      </div>

      {r.community_handle_url && (
        <div className="px-4 pb-3">
          <a
            href={r.community_handle_url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-[11px] text-[#00F0FF] hover:underline truncate"
          >
            <ExternalLink className="w-3 h-3 shrink-0" />
            <span className="truncate">{prettyHandle(r.community_handle_url)}</span>
          </a>
        </div>
      )}

      <div className="mt-auto p-4 pt-2 border-t border-white/5">
        {canJoin ? (
          <Link
            to={`/r/${encodeURIComponent(r.room_id)}`}
            className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-[11px] font-bold uppercase tracking-[0.14em] bg-gradient-to-r from-[#FF007F] to-[#00F0FF] text-[#0A0A0F] hover:brightness-110 transition"
            data-testid={`rooms-card-${slug}-join`}
          >
            Join Room <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        ) : (
          <div
            className="w-full text-center px-4 py-2 rounded-lg text-[10px] uppercase tracking-[0.14em] text-white/40 bg-white/[0.03] border border-white/8"
            data-testid={`rooms-card-${slug}-setup`}
          >
            Setting up — check back soon
          </div>
        )}
      </div>
    </div>
  );
}


function LiveCountChip({ count }) {
  // R14-F — live participant count chip. Green when >0, gray when empty.
  const isLive = (count || 0) > 0;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-[0.14em] ${
        isLive ? "bg-emerald-500/15 text-emerald-300" : "bg-white/5 text-white/40"
      }`}
      title={isLive ? `${count} people in room right now` : "Empty"}
      data-testid="rooms-live-count"
    >
      <Users className="w-2.5 h-2.5" />
      {isLive ? `${count} in room` : "Empty"}
    </span>
  );
}


function StatusPill({ status }) {
  const map = {
    inactive:  { bg: "bg-white/8",        fg: "text-white/55",  label: "Inactive" },
    available: { bg: "bg-emerald-500/15", fg: "text-emerald-300", label: "Available" },
    live:      { bg: "bg-[#00F0FF]/15",   fg: "text-[#00F0FF]", label: "Live" },
    ended:     { bg: "bg-rose-500/15",    fg: "text-rose-300",  label: "Ended" },
  };
  const s = map[status] || map.inactive;
  return (
    <span className={`inline-flex px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-[0.16em] ${s.bg} ${s.fg}`}>
      {s.label}
    </span>
  );
}
